import * as React from 'react';
import { useFieldArray } from 'react-hook-form';

import { Box } from '@lightspeed/flame/Core';

import { useLabelPrintForm } from './hooks';
import { StyledDescription, StyledLabelWithoutMargin } from './HubLabelPrintBody.style';
import { StyledButton, StyledError, StyledInput, StyledInputGroup } from './InputStepper.style';

interface InputStepperProps {
  labelText: string;
  descriptionText?: string;
  inputRef:
    | 'quantity.labels'
    | 'default'
    | 'labelMargins.small'
    | 'labelMargins.normal'
    | 'labelMargins.alt'
    | 'labelMargins.jewelry';
  isInputDisabled?: boolean;
  isIncrementButtonDisabled?: boolean;
  isDecrementButtonDisabled?: boolean;
  isInputRequired?: boolean;
  maxInputValue: number;
  minInputValue: number;
  printForm: ReturnType<typeof useLabelPrintForm>;
  placeholder?: string | undefined;
  status?: undefined | 'error';
  stepperFunction: (amount: number, direction: 'top' | 'left' | undefined) => () => void;
  labelMargin?: 'left' | 'top';
}

export const InputStepper: React.FC<InputStepperProps> = ({
  labelText,
  descriptionText,
  inputRef,
  isInputDisabled = false,
  isIncrementButtonDisabled = false,
  isDecrementButtonDisabled = false,
  maxInputValue,
  minInputValue,
  printForm,
  placeholder = '0',
  status = undefined,
  stepperFunction,
  labelMargin,
}) => {
  const { control, register, getValues } = printForm;
  const { fields } = useFieldArray({
    control,
    name: 'fieldArray',
  });

  const inputReference = labelMargin ? `${inputRef}.${labelMargin}` : inputRef;

  const renderInputs = () =>
    fields
      .filter((field) => {
        if (field.name === inputReference) return true;
        // eslint-disable-next-line no-else-return
        else {
          register(field.name);
          return false;
        }
      })
      .map((field) => (
        <StyledInput
          data-test={inputRef}
          type="number"
          key={field.id}
          id="input-stepper"
          readOnly={isInputDisabled}
          disabled={isInputDisabled}
          placeholder={placeholder}
          defaultValue={getValues(field.name) as number}
          status={status}
          {...register(field.name, {
            max: maxInputValue,
            min: minInputValue,
            valueAsNumber: true,
          })}
        />
      ));

  return (
    <>
      <Box mb={1}>
        <label htmlFor="input-stepper">
          <StyledLabelWithoutMargin>{labelText}</StyledLabelWithoutMargin>
        </label>
        <StyledDescription>{descriptionText}</StyledDescription>
      </Box>
      <StyledInputGroup>
        <StyledButton
          data-test={`input-stepper-button-decrement-${inputReference}`}
          onClick={stepperFunction(-1, labelMargin)}
          disabled={isDecrementButtonDisabled}
        >
          &#8722;
        </StyledButton>
        {renderInputs()}
        <StyledButton
          data-test={`input-stepper-button-increment-${inputReference}`}
          onClick={stepperFunction(1, labelMargin)}
          disabled={isIncrementButtonDisabled}
        >
          &#43;
        </StyledButton>
      </StyledInputGroup>
      <Box>
        {labelMargin && status && (
          <StyledError data-test="label-margins-error">
            Ensure that the label margins are between -20 and 20.
          </StyledError>
        )}
      </Box>
    </>
  );
};
